import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, InputNumber, Row, Space, Typography } from 'antd';
import { CustomerApi } from 'src/api';
import { IDiscountParams } from 'main/interfaces';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { handleChangeStatus } from 'src/helpers';

interface ICustomerEditDiscountProps {
  customer_id: string;
  loading: boolean;
}

export const CustomerEditDiscount: React.FC<ICustomerEditDiscountProps> = ({ customer_id, loading }) => {
  const [discount, setDiscount] = useState<IDiscountParams | null>(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [form] = Form.useForm();

  const getDiscount = useCallback((): void => {
    setDiscountLoading(true);
    CustomerApi.getCustomerDiscount(customer_id).then((res) => {
      if (res?.data) {
        setDiscount(res.data);
      } else {
        setDiscount(null);
      }
      setDiscountLoading(false);
    }).catch(() => {
      setDiscount(null);
      setDiscountLoading(false);
    });
  }, [customer_id]);

  const handleAddDiscount = useCallback(async (values: { value: number }): Promise<void> => {
    setDiscountLoading(true);
    const res = await CustomerApi.addCustomerDiscount(customer_id, values);
    handleChangeStatus<IDiscountParams>(res);
    if (res?.data) {
      setDiscount(res.data);
      form.resetFields();
    }
    setDiscountLoading(false);
  }, [customer_id, form]);

  const handleDeleteDiscount = useCallback(async (): Promise<void> => {
    setDiscountLoading(true);
    const res = await CustomerApi.deleteCustomerDiscount(customer_id);
    handleChangeStatus<Record<string, string>>(res);
    if (res?.data) {
      setDiscount(null);
    }
    setDiscountLoading(false);
  }, [customer_id]);

  useEffect(() => {
    getDiscount();
  }, [getDiscount]);

  if (discountLoading && !discount) {
    return <Typography.Text>Завантаження...</Typography.Text>;
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {discount ? (
        <Row gutter={30}>
          <Col xs={24} sm={24} md={6} lg={5}>
            <Typography.Title level={5}>Поточна знижка</Typography.Title>
            <Typography.Text strong style={{ fontSize: '18px' }}>
              {`${discount.value}%`}
            </Typography.Text>
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', marginTop: '10px' }}>
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={handleDeleteDiscount}
              loading={discountLoading}
              disabled={loading}
            >
              Видалити знижку
            </Button>
          </Col>
        </Row>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddDiscount}
          disabled={loading || discountLoading}
        >
          <Row gutter={30}>
            <Col xs={24} sm={24} md={6} lg={5}>
              <Form.Item
                name="value"
                label="Відсоток знижки"
                rules={[
                  { required: true, message: 'Поле повинно бути заповнене' },
                  { type: 'number', min: 1, max: 100, message: 'Знижка повинна бути від 1 до 100%' }
                ]}
                hasFeedback
              >
                <InputNumber
                  min={1}
                  max={100}
                  style={{ width: '100%' }}
                  placeholder="Введіть відсоток знижки"
                  addonAfter="%"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} style={{ display: 'flex', alignItems: 'start', justifyContent: 'flex-start' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                htmlType="submit"
                loading={discountLoading}
                disabled={loading}
              >
                Додати знижку
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Space>
  );
};
